import React, { useState } from "react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";

const NewAppointment = () => {
    const [option, setOption] = useState("");
    const [step, setStep] = useState(1);
    const scrollRef = useRef(null);
    const [formData, setFormData] = useState({
        prefix: "",
        firstName: "",
        middleName: "",
        lastName: "",
        dob: "",
        gender: "",
        phone: "",
    });

    const [appointmentData, setAppointmentData] = useState({
        hospital: "",
        specialty: "",
        doctor: "",
        date: "",
        slot: "",
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAppointmentChange = (e) => {
        setAppointmentData({
            ...appointmentData,
            [e.target.name]: e.target.value,
        });
    };

    const handleNext = () => {
        setStep(2);
    };

    const handleCancel = () => {
        setOption("");
        setFormData({
            prefix: "",
            firstName: "",
            middleName: "",
            lastName: "",
            dob: "",
            gender: "",
            phone: "",
        });
        setStep(1);
    };

    const isConfirmEnabled = Object.values(appointmentData).every(
        (value) => value !== ""
    );

    const dates = [
        { id: 1, day: "Mon", num: 1 },
        { id: 2, day: "Tue", num: 2 },
        { id: 3, day: "Wed", num: 3 },
        { id: 4, day: "Thu", num: 4 },
        { id: 5, day: "Fri", num: 5 },
        { id: 6, day: "Sat", num: 6 },
        { id: 7, day: "Sun", num: 7 },
        // Add more dates as needed
    ];

    // State for selected date
    const [selectedDate, setSelectedDate] = useState(null);

    // Handler for date selection
    const handleDateSelect = (date) => {
        setSelectedDate(date.id);
    };

    // Handler for month selection
    const handleMonthChange = (e) => {
        const selectedMonth = e.target.value;
        console.log("Selected Month:", selectedMonth);
        // Generate new dates array based on selected month
    };

    const scroll = (direction) => {
        const { current } = scrollRef;
        if (current) {
          current.scrollBy({
            left: direction === "left" ? -150 : 150,
            behavior: "smooth",
          });
        }
      };

    return (
        <>
            <Navbar />
            <div className="max-w-6xl px-6 py-10 mx-auto bg-white rounded-lg shadow-md">
                <h2 className="mb-6 text-2xl font-semibold text-gray-700">
                    Book Appointment
                </h2>

                {/* Always Visible Option Buttons */}
                <div className="flex items-center mb-8 space-x-6">
                    <button
                        onClick={() => setOption("new")}
                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-150 
              ${
                  option === "new"
                      ? "bg-teal-100 text-teal-600 border-teal-400 hover:bg-teal-200"
                      : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
              }`}
                    >
                        {option === "new" && (
                            <span className="flex items-center justify-center w-5 h-5 ml-3 text-white bg-teal-600 rounded-full">
                                <Check size={14} strokeWidth={3} />
                            </span>
                        )}
                        <span className="ml-2">I am new to Zydus</span>
                    </button>
                    <button
                        onClick={() => setOption("existing")}
                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-150 
              ${
                  option === "existing"
                      ? "bg-teal-100 text-teal-600 border-teal-400 hover:bg-teal-200"
                      : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
              }`}
                    >
                        {option === "existing" && (
                            <span className="flex items-center justify-center w-5 h-5 ml-3 text-white bg-teal-600 rounded-full">
                                <Check size={14} strokeWidth={3} />
                            </span>
                        )}
                        <span className="ml-2">Already registered patient</span>
                    </button>
                </div>

                {/* New Patient Form Step 1 */}
                {option === "new" && step === 1 && (
                    <div className="mt-10">
                        <div className="flex justify-center mb-6">
                            <div className="flex items-center">
                                <div className="flex items-center text-teal-600">
                                    <div className="flex items-center justify-center w-6 h-6 text-white bg-teal-600 rounded-full">
                                        1
                                    </div>
                                    <span className="ml-2 text-sm font-medium">
                                        Patient Details
                                    </span>
                                </div>
                                <div className="w-10 h-px mx-3 bg-teal-300"></div>
                                <div className="flex items-center text-gray-400">
                                    <div className="flex items-center justify-center w-6 h-6 border border-gray-400 rounded-full">
                                        2
                                    </div>
                                    <span className="ml-2 text-sm font-medium">
                                        Appointment
                                    </span>
                                </div>
                            </div>
                        </div>
                        <form className="space-y-6">
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        Prefix
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="prefix"
                                        value={formData.prefix}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-teal-500"
                                    >
                                        <option value="">Select</option>
                                        <option value="Mr.">Mr.</option>
                                        <option value="Mrs.">Mrs.</option>
                                        <option value="Miss">Miss</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        First Name
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="firstName"
                                        type="text"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        placeholder="First Name (required)"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        Middle Name
                                    </label>
                                    <input
                                        name="middleName"
                                        type="text"
                                        value={formData.middleName}
                                        onChange={handleChange}
                                        placeholder="Middle Name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        Last Name
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="lastName"
                                        type="text"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        placeholder="Last Name (required)"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        Date of Birth
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="dob"
                                        type="date"
                                        value={formData.dob}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        Gender
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="">Select</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-600">
                                        Phone Number
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <span className="px-3 py-2 text-sm text-gray-600 bg-gray-100 border border-gray-300 rounded-md">
                                            +91
                                        </span>
                                        <input
                                            name="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="00000-00000"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-center mt-6 space-x-4">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-5 py-2 font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="px-6 py-2 font-medium text-white rounded-md bg-gradient-to-r from-pink-500 to-teal-500 hover:opacity-90"
                                >
                                    Save & Next
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Step 2: Appointment UI */}
                {option === "new" && step === 2 && (
                    <div className="space-y-6">
                        <h3 className="text-xl font-semibold text-gray-800">
                            Appointment Details
                        </h3>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            {/* Hospital Dropdown */}
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-600">
                                    Hospital
                                </label>
                                <select
                                    name="hospital"
                                    value={appointmentData.hospital}
                                    onChange={handleAppointmentChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">Select Hospital</option>
                                    <option value="Apollo Hospital">
                                        Apollo Hospital
                                    </option>
                                    <option value="Fortis">Fortis</option>
                                    <option value="Max Healthcare">
                                        Max Healthcare
                                    </option>
                                </select>
                            </div>

                            {/* Specialty Dropdown */}
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-600">
                                    Specialty
                                </label>
                                <select
                                    name="specialty"
                                    value={appointmentData.specialty}
                                    onChange={handleAppointmentChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">Select Specialty</option>
                                    <option value="Cardiology">
                                        Cardiology
                                    </option>
                                    <option value="Orthopedics">
                                        Orthopedics
                                    </option>
                                    <option value="Dermatology">
                                        Dermatology
                                    </option>
                                </select>
                            </div>

                            {/* Doctor Dropdown */}
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-600">
                                    Doctor
                                </label>
                                <select
                                    name="doctor"
                                    value={appointmentData.doctor}
                                    onChange={handleAppointmentChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">Select Doctor</option>
                                    <option value="Dr. Sharma">
                                        Dr. Sharma
                                    </option>
                                    <option value="Dr. Mehta">Dr. Mehta</option>
                                    <option value="Dr. Verma">Dr. Verma</option>
                                </select>
                            </div>

                            {/* Date Picker */}
                        </div>
                        <div className="max-w-md mx-auto">
                                {/* Month Selection Dropdown */}
                                <div className="flex items-center justify-center">
                                    <select
                                        name="month"
                                        //   value={selectedMonth}
                                        onChange={handleMonthChange}
                                        className="px-4 py-2 transition duration-300 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Select Month</option>
                                        <option value="January">January</option>
                                        <option value="February">
                                            February
                                        </option>
                                        <option value="March">March</option>
                                        <option value="April">April</option>
                                        <option value="May">May</option>
                                        <option value="June">June</option>
                                        <option value="July">July</option>
                                        <option value="August">August</option>
                                        <option value="September">
                                            September
                                        </option>
                                        <option value="October">October</option>
                                        <option value="November">
                                            November
                                        </option>
                                        <option value="December">
                                            December
                                        </option>
                                    </select>
                                </div>

                                <div className="relative w-full">
                                    {/* Left Arrow */}
                                    <button
                                        onClick={() => scroll("left")}
                                        className="absolute z-10 p-1 text-gray-600 transition -translate-y-1/2 bg-white rounded-full shadow-md left-2 top-1/2 hover:bg-gray-100"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>

                                    {/* Scrollable Dates */}
                                    <div
                                        ref={scrollRef}
                                        className="px-10 overflow-x-auto whitespace-nowrap scrollbar-hide"
                                        style={{ scrollBehavior: "smooth" }}
                                    >
                                        <div className="inline-flex py-3 space-x-3">
                                        {dates.map((date) => {
                                            const isSelected = selectedDate === date.id;
                                            return (
                                            <div
                                                key={date.id}
                                                onClick={() => handleDateSelect(date)}
                                                className={`flex flex-col items-center justify-center min-w-[4.5rem] h-16 border rounded-xl cursor-pointer transition duration-300 ${
                                                isSelected
                                                    ? "bg-[#009DA5] border-blue-600 shadow-lg"
                                                    : "bg-white border-gray-300 hover:border-[#009DA5] hover:shadow"
                                                }`}
                                            >
                                                <span
                                                className={`text-sm font-medium ${
                                                    isSelected ? "text-white" : "text-gray-500"
                                                }`}
                                                >
                                                {date.day}
                                                </span>
                                                <span
                                                className={`text-lg font-bold ${
                                                    isSelected ? "text-white" : "text-gray-700"
                                                }`}
                                                >
                                                {date.num}
                                                </span>
                                            </div>
                                            );
                                        })}
                                        </div>
                                    </div>

                                    {/* Right Arrow */}
                                    <button
                                        onClick={() => scroll("right")}
                                        className="absolute z-10 p-1 text-gray-600 transition -translate-y-1/2 bg-white rounded-full shadow-md right-2 top-1/2 hover:bg-gray-100"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                    </div>


                                {/* Slot Dropdown */}
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">
                                        Slot
                                    </label>
                                    <select
                                        name="slot"
                                        value={appointmentData.slot}
                                        onChange={handleAppointmentChange}
                                        className="w-full px-4 py-3 transition duration-300 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="" disabled>
                                            Select Slot
                                        </option>
                                        <option value="9:00 AM">9:00 AM</option>
                                        <option value="11:00 AM">
                                            11:00 AM
                                        </option>
                                        <option value="2:00 PM">2:00 PM</option>
                                    </select>
                                </div>
                            </div>

                        <div className="flex justify-center">
                            <button
                                className={`px-6 py-2 font-medium text-white rounded-md ${
                                    isConfirmEnabled
                                        ? "bg-gradient-to-r from-green-500 to-teal-500 hover:opacity-90"
                                        : "bg-gray-400 cursor-not-allowed"
                                }`}
                                disabled={!isConfirmEnabled}
                            >
                                Confirm Appointment
                            </button>
                        </div>
                    </div>
                )}

                {/* Existing Patient Section */}
                {option === "existing" && (
                    <div className="mt-8 text-gray-600">
                        <p>
                            Search for patient or login functionality goes here.
                        </p>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default NewAppointment;
